// Активности СВ («Моё планирование»): план и факт по дням. Только учёт, на KPI не влияет.
import { supabase } from './supabase';
import { addDays } from './alerts';
import { selectAll } from './data';

export const ACT = {
  per: { label: 'Перекрытие',    short: 'Пер', bg: '#C6EFCE', fg: '#276221', border: 'transparent', cnt: 'визитов' },
  sov: { label: 'Совтур',        short: 'Совт', bg: '#FFE8CC', fg: '#9C4A00', border: 'transparent', sov: true },
  st:  { label: 'Стажировка МЕ',  short: 'Ст',  bg: '#D6E4F0', fg: '#1F5F9C', border: 'transparent', cnt: 'визитов', cand: true },
  stm: { label: 'Стажировка МЕ−', short: 'Ст−', bg: '#EBF4FF', fg: '#1F5F9C', border: '#8DB4DA', dashed: true, cand: true },
  of:  { label: 'Офисный день',   short: 'Оф',  bg: '#EDF2F7', fg: '#4A5568', border: 'transparent' },
  au:  { label: 'Аудиты СВ',      short: 'Ауд', bg: '#E9E1F7', fg: '#4a3aa7', border: 'transparent', cnt: 'аудитов' },
  asm: { label: 'Аудит с АСМ',   short: 'Ауд-АСМ', bg: '#D3F0EE', fg: '#0F6B63', border: 'transparent' },
  ot:  { label: 'Отпуск',         short: 'Отп', bg: '#FFF9C4', fg: '#7D5A00', border: 'transparent' },
  sick: { label: 'Больничный',    short: 'Бол', bg: '#FDE2E2', fg: '#9B2C2C', border: 'transparent' },
};
export const ACT_ORDER = ['per', 'sov', 'st', 'stm', 'of', 'au', 'asm', 'ot', 'sick'];

const COLS = 'id, supervisor_id, work_date, kind, activity, cnt, candidate, source, route_id';

export async function getActivities(svIds, from, to) {
  if (!svIds || !svIds.length) return [];
  const out = [];
  for (let i = 0; i < svIds.length; i += 100) {
    const { data, error } = await supabase.from('sv_activities').select(COLS)
      .in('supervisor_id', svIds.slice(i, i + 100)).gte('work_date', from).lte('work_date', to).limit(20000);
    if (error) throw new Error(error.message);
    out.push(...(data || []));
  }
  return enrichActs(out);
}

// Перекрытие с маршрутом: заказ / план / факт ТОЛЬКО читаются из Табеля (orders, daily_statuses, daily_visit_facts).
// Ничего не пишется обратно, поэтому прогнозы и зачёты не затрагиваются.
// Значение визитов: в «плане» — план из Табеля (иначе заказ), в «факте» — факт из Табеля.
export async function getRouteDays(routeIds, from, to) {
  const ids = [...new Set(routeIds)];
  const res = {};
  const slot = (r, d) => (res[`${r}|${d}`] = res[`${r}|${d}`] || {});
  for (let i = 0; i < ids.length; i += 40) {
    const part = ids.slice(i, i + 40);
    const f = q => q.in('route_id', part).gte('work_date', from).lte('work_date', to);
    const [o, st, fa] = await Promise.all([
      selectAll('orders', 'route_id, work_date, visits', f).catch(() => []),
      selectAll('daily_statuses', 'route_id, work_date, status_code, plan_visits', f).catch(() => []),
      selectAll('daily_visit_facts', 'route_id, work_date, visits_fact', f).catch(() => []),
    ]);
    o.forEach(x => { slot(x.route_id, x.work_date).order = x.visits; });
    st.forEach(x => { const t = slot(x.route_id, x.work_date); t.plan = x.plan_visits; t.status = x.status_code; });
    fa.forEach(x => { slot(x.route_id, x.work_date).fact = x.visits_fact; });
  }
  return res;
}

export function routeVisits(a, info) {
  if (!info) return null;
  if (a.kind === 'fact') return info.fact ?? null;
  return info.plan ?? info.order ?? null;
}

export async function enrichActs(acts) {
  const withRoute = acts.filter(a => a.activity === 'per' && a.route_id);
  if (!withRoute.length) return acts;
  const dates = withRoute.map(a => a.work_date).sort();
  let days = {};
  try { days = await getRouteDays(withRoute.map(a => a.route_id), dates[0], dates[dates.length - 1]); }
  catch (e) { console.error('[enrichActs]', e?.message || e); }
  return acts.map(a => {
    if (a.activity !== 'per' || !a.route_id) return a;
    const info = days[`${a.route_id}|${a.work_date}`] || {};
    return { ...a, info, cnt: routeVisits(a, info) };
  });
}

export async function addActivity({ svId, date, kind, activity, cnt = null, candidate = null, userId = null, routeId = null }) {
  const { data, error } = await supabase.from('sv_activities').insert({
    supervisor_id: svId, work_date: date, kind, activity,
    cnt: cnt === '' ? null : cnt, candidate: candidate || null, source: 'manual', created_by: userId, route_id: routeId,
  }).select(COLS).single();
  if (error) throw new Error(error.message);
  return data;
}

export async function updateActivity(id, patch) {
  const { data, error } = await supabase.from('sv_activities')
    .update({ ...patch, updated_at: new Date().toISOString() }).eq('id', id).select(COLS).single();
  if (error) throw new Error(error.message);
  return (await enrichActs([data]))[0];
}

export async function deleteActivity(id) {
  const { error } = await supabase.from('sv_activities').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export async function loadSvFacts(rows, actorId) {
  const { data, error } = await supabase.rpc('load_sv_facts', { p_rows: rows, p_actor: actorId });
  if (error) throw new Error(error.message);
  return data;
}

// Для дня «эффективные» активности: факт, если он есть, иначе план.
export function effectiveByDay(acts) {
  const m = {};
  acts.forEach(a => {
    const d = (m[a.work_date] = m[a.work_date] || { plan: [], fact: [] });
    d[a.kind].push(a);
  });
  const res = {};
  Object.keys(m).forEach(d => { res[d] = m[d].fact.length ? m[d].fact : m[d].plan; });
  return res;
}

function isWorkDay(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  const w = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return w !== 0 && w !== 6;
}

// Сводка по одному СВ за диапазон [from..to]; рабочие дни считаем только до today.
export function summarize(acts, from, to, today) {
  const eff = effectiveByDay(acts.filter(a => a.work_date >= from && a.work_date <= to));
  const s = { filled: 0, workDays: 0, per: 0, perVisits: 0, st: 0, stm: 0, sov: 0, asm: 0, sick: 0, au: 0, auCnt: 0, of: 0, ot: 0 };
  const end = to < today ? to : today;
  for (let d = from; d <= end; d = addDays(d, 1)) if (isWorkDay(d)) s.workDays++;
  Object.keys(eff).forEach(d => {
    const list = eff[d];
    if (!list.length) return;
    if (d <= end && isWorkDay(d)) s.filled++;
    list.forEach(a => {
      if (a.activity === 'per') { s.per++; s.perVisits += a.cnt || 0; }
      else if (a.activity === 'sov') s.sov++;
      else if (a.activity === 'asm') s.asm++;
      else if (a.activity === 'sick') s.sick++;
      else if (a.activity === 'st') s.st++;
      else if (a.activity === 'stm') s.stm++;
      else if (a.activity === 'au') { s.au++; s.auCnt += a.cnt || 0; }
      else if (a.activity === 'of') s.of++;
      else if (a.activity === 'ot') s.ot++;
    });
  });
  return s;
}
